import React, { useEffect, useRef, useContext, useState } from 'react';
import debounce from 'lodash/debounce';
import { GlobalContext } from '../../services/context/DataContext';
import rightArrow from '../1elements/Icons/png/right-arrow.png';
import useGMap from '../../services/GMap/useGMap';
import { useBoundInfo } from '../../services/GMap/useBoundInfo';
import { mapIconListForGoogleMap } from '../../consts/mapIconList';
import { useTheme } from '@mui/material/styles';
import { buildLocationPopup } from '../../services/GMap/locationPopup';
import {
  getCustomMapMarker,
  getCustomMapMarkerAlt
} from '../../common/dynamicCustomizationService';
import { getIconIndex } from '../../services/util/formatter';
import { getExecutiveMarkerUrl } from '../../services/GMap/executiveMarker';
import { MAP_EVENTS } from '../../consts/map-events';
import { zoomToMapPosition } from '../../services/GMap/zoomToMapPosition';

const GMap = () => {
  const {
    locdata,
    selectedText,
    id,
    markersData,
    mapMarkerSettingData,
    mapMarkerUserSettingData,
    latCoord,
    lngCoord,
    collapseIsOpenData
  } = useContext(GlobalContext);
  const [, setLat] = latCoord;
  const [, setLng] = lngCoord;
  const [data] = locdata;
  const [markers, setMarkers] = markersData;
  const [selectedLoc] = selectedText;
  const [, setLocId] = id;
  const [mapMarkerSetting, setMapMarkerSetting] = mapMarkerSettingData;
  const [mapMarkerUserSetting] = mapMarkerUserSettingData;
  const ref = useRef(null);
  const [map, setMap] = useState();
  const [finalObj, setFinalObj] = useState([]);
  const zoneOverlaysRef = useRef([]);

  const execPolygonsRef = useRef([]);
  const execPinsRef = useRef([]);

  const [mapMarkerSize, setMapMarkerSize] = useState(40);
  const mapMarkerSizeRef = useRef(40);
  const mapListenersRef = useRef([]);

  const infoWindow = new window.google.maps.InfoWindow({ content: '' });
  const theme = useTheme();
  const [collapseIsOpen, setCollapseIsOpen] = collapseIsOpenData;

  useGMap(ref, map, setMap);
  useBoundInfo(map);

  useEffect(() => {
    const getIconMappingFromLS = () => {
      const iconMappingSystem = window.localStorage.getItem('iconMapping')
        ? JSON.parse(window.localStorage.getItem('iconMapping'))
        : {};
      const iconMappingUser = window.localStorage.getItem('iconMappingUser')
        ? JSON.parse(window.localStorage.getItem('iconMappingUser'))
        : {};
      setMapMarkerSetting({ ...iconMappingSystem, ...iconMappingUser });
    };
    getIconMappingFromLS();
    // eslint-disable-next-line
  }, []);

  const parseWktPolygonToPaths = (wkt) => {
    if (!wkt || typeof wkt !== 'string') return [];
    const m = wkt.match(/^POLYGON\s*\(\(\s*([^)]+)\s*\)\)\s*$/i);
    if (!m) return [];
    const ring = m[1].split(',').map(pair => {
      const [lngStr, latStr] = pair.trim().split(/\s+/);
      return { lat: Number(latStr), lng: Number(lngStr) };
    });
    return [ring];
  };

  const parseWktToPolygons = (wkt) => {
    if (!wkt || typeof wkt !== 'string') return [];
    const clean = wkt.trim().toUpperCase();

    if (clean.startsWith('POLYGON')) {
      const m = wkt.match(/^POLYGON\s*\(\s*\(([^)]+)\)\s*\)\s*$/i);
      if (!m) return [];
      const ring = m[1].split(',').map(pair => {
        const [lngStr, latStr] = pair.trim().split(/\s+/);
        return { lat: Number(latStr), lng: Number(lngStr) };
      });
      return [[ring]];
    }

    if (clean.startsWith('MULTIPOLYGON')) {
      const body = wkt.replace(/^MULTIPOLYGON\s*\(\s*/i, '').replace(/\)\s*$/,'');
      const polys = [];
      let depth = 0, cur = '';
      for (const ch of body) {
        if (ch === '(') depth++;
        if (ch === ')') depth--;
        cur += ch;
        if (depth === 0 && cur.trim()) {
          polys.push(cur.trim().replace(/^\(|\)$/g, ''));
          cur = '';
        }
      }
      return polys.map(polyStr => {
        const inner = polyStr.replace(/^\(|\)$/g,'');
        const rings = [];
        let d = 0, c = '';
        for (const ch of inner) {
          if (ch === '(') d++;
          if (ch === ')') d--;
          c += ch;
          if (d === 0 && c.trim()) {
            const ringStr = c.trim().replace(/^\(|\)$/g,'');
            const ring = ringStr.split(',').map(pair => {
              const [lngStr, latStr] = pair.trim().split(/\s+/);
              return { lat: Number(latStr), lng: Number(lngStr) };
            });
            rings.push(ring);
            c = '';
          }
        }
        return rings;
      });
    }
    return [];
  };

  const geojsonPolygonToPaths = (coords) => {
    if (!Array.isArray(coords)) return [];
    const rings = coords[0] || [];
    return [rings.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }))];
  };
  const geojsonMultiPolygonToPaths = (coords) => {
    if (!Array.isArray(coords)) return [];
    const paths = [];
    coords.forEach(poly => {
      const outer = poly?.[0] || [];
      const ring = outer.map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }));
      if (ring.length) paths.push(ring);
    });
    return paths.length ? [paths.flat()] : [];
  };

  useEffect(() => {
    clearMarkers();
    const generateLatLongCandidates = () => {
      return data
        .filter((record) => record.location_latitude && record.location_longitude)
        .map((item) => ({
          buildingName: item.name,
          locationArray: [item],
          lat: item.location_latitude,
          lang: item.location_longitude,
          buildingAddress: item.building_address,
          buildingReference: 'no transit',
          customerName: item.name,
          entityTypeId: item.entityTypeId,
          industryTypeId: item.industryTypeId,
          id: item.id
        }));
    };

    const generateAddressBasedCandidates = () => {
      data &&
        data
          .filter((record) => !record.location_latitude || !record.location_longitude)
          .forEach((dataItem) => {
            const {
              name, lat, long, id, building_address, building_reference,
              customer_name, type, entityTypeId, industryTypeId
            } = dataItem;

            const key = `${name},${lat},${long},${id},${building_address},${building_reference},${customer_name},${type},${entityTypeId},${industryTypeId}`;
            const newElement = dataItem;

            if (holder.hasOwnProperty(key)) {
              Array.isArray(holder[key].locationArray)
                ? holder[key].locationArray.push(newElement)
                : (holder[key].locationArray = [newElement]);
            } else {
              holder[key] = {
                locationArray: [newElement],
                buildingName: name,
                lat: lat,
                lang: long,
                id,
                buildingAddress: building_address,
                buildingReference: building_reference,
                customerName: customer_name,
                type: type,
                entityTypeId: entityTypeId,
                industryTypeId: industryTypeId
              };
            }
          });

      return Object.values(holder);
    };

    const generateFinalObject = () => {
      const addressCan = generateAddressBasedCandidates();
      const latLongCan = generateLatLongCandidates();
      return addressCan.concat(latLongCan);
    };

    if (selectedLoc.trim() !== '') {
      setFinalObj(
        generateFinalObject().filter((item) => {
          if (
            (item.buildingName === 'null' && item.locationArray.some(location => `${location.name} ${location.loc_transit_id} ${location.id}`.includes(selectedLoc))) ||
            item.buildingName === selectedLoc
          ) return item;
          return false;
        })
      );
    } else {
      setFinalObj(generateFinalObject());
    }
    // eslint-disable-next-line
  }, [data, selectedLoc]);

  const holder = {};

  const locDetails = (x, popupId) => {
    window.pop = (id) => {
      const safe = String(id).trim().replace(/[^a-z0-9-]/gi, '');
      setLocId(safe);
      window.localStorage.setItem('locId', safe);
    };
    return buildLocationPopup(x, popupId, rightArrow);
  };

  const setupMarkerSizeByZoom = debounce((zoom, baseSize = 40) => {
    const minSize = baseSize;
    const maxSize = baseSize * 2.5;
    const ZOOM_THRESHOLD = 7;
    const ZOOM_RANGE = 13;
    const normalizedZoom = Math.max(1, Math.min(20, zoom));

    let size;
    if (normalizedZoom < ZOOM_THRESHOLD) {
      size = minSize;
    } else {
      const scaleFactor = (normalizedZoom - ZOOM_THRESHOLD) / ZOOM_RANGE;
      size = minSize + (maxSize - minSize) * scaleFactor;
    }

    setMapMarkerSize(Math.round(size));
    mapMarkerSizeRef.current = Math.round(size);
  }, 300);

  const mapMarker = () => {
    if (!map) {
      clearMarkers();
      return;
    }
    setupMarkerSizeByZoom(map.getZoom());

    if (finalObj.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      mapListenersRef.current.push(
        window.google.maps.event.addListener(map, 'zoom_changed', function () {
          setupMarkerSizeByZoom(map.getZoom());
        })
      );

      const systemMarkerSettings = {};

      finalObj.map((x, index) => {
        let customMarker = mapIconListForGoogleMap(
          theme.palette.primary.accent,
          'proximity',
          false
        )[23];
        let customMarkerHover = mapIconListForGoogleMap(
          theme.palette.primary.accent,
          'proximity',
          true
        )[23];

        const markerTypeColor = mapMarkerSetting.industryTypeIcons[x.industryTypeId]?.color ||
          mapMarkerSetting.entityTypeColors[x.entityTypeId]?.color;
        const markerTypeIcon = mapMarkerSetting.industryTypeIcons[x.industryTypeId]?.icon;
        const markerTypeStyle = mapMarkerSetting.industryTypeIcons[x.industryTypeId]?.style;

        const markerUserSetting = mapMarkerUserSetting[x.id];
        const markerDefaultSetting = mapMarkerSetting[x.id] || mapMarkerSetting['Default Marker'];

        if (markerDefaultSetting || markerUserSetting) {
          const iconColor = markerUserSetting?.color || markerTypeColor || markerDefaultSetting?.color;
          const iconIndex = markerUserSetting?.icon || markerTypeIcon || markerDefaultSetting?.icon;
          const iconStyle = markerUserSetting?.style || markerTypeStyle || markerDefaultSetting?.style;

          if (!mapMarkerSetting[x.id]) {
            systemMarkerSettings[x.id] = {
              icon: iconIndex,
              color: iconColor,
              style: iconStyle
            };
          }

          const iconsList = mapIconListForGoogleMap(iconColor, iconStyle);
          const iconsListHover = mapIconListForGoogleMap(iconColor, iconStyle, true);
          const idx = typeof iconIndex === 'number' ? iconIndex : getIconIndex(iconIndex);
          customMarker = iconsList[idx];
          customMarkerHover = iconsListHover[idx];

          if (!customMarker) customMarker = getCustomMapMarker();
          if (!customMarkerHover) customMarkerHover = getCustomMapMarkerAlt();
        }

        if (!isNaN(x.lat) && !isNaN(x.lang)) {
          const marker = new window.google.maps.Marker({
            position: { lat: Number(x.lat), lng: Number(x.lang) },
            map,
            icon: {
              url: customMarker,
              scaledSize: new window.google.maps.Size(mapMarkerSizeRef.current, mapMarkerSizeRef.current)
            }
          });
          marker.assetId = x.id;
          const id = 'infowindowContent-' + index;
          const contentString = locDetails(x, id);

          function zoomToSelectedLocation() {
            const target = marker.getPosition();
            zoomToMapPosition(map, target);
          }

          window.google.maps.event.addListener(infoWindow, 'domready', function () {
            window.google.maps.event.clearListeners(infoWindow, 'domready');
            const ref = document.getElementById(id);
            if (ref) ref.addEventListener('click', zoomToSelectedLocation);
          });

          if (x.locationArray.length === 1) {
            marker.addListener('click', () => {
              const id = x.id;
              setLocId(id);
              window.localStorage.setItem('locId', id);
              zoomToSelectedLocation();
            });
          }

          marker.addListener('mouseout', () => {
            marker.setIcon({
              url: customMarker,
              scaledSize: new window.google.maps.Size(mapMarkerSizeRef.current, mapMarkerSizeRef.current)
            });
          });
          marker.addListener('mouseover', () => {
            const newSize = !isNaN(mapMarkerSizeRef.current) ? mapMarkerSizeRef.current + 10 : 50;
            marker.setIcon({
              url: customMarkerHover,
              scaledSize: new window.google.maps.Size(newSize, newSize)
            });
            infoWindow.setContent(contentString);
            infoWindow.open(map, marker);
          });
          mapListenersRef.current.push(
            window.google.maps.event.addListener(map, 'click', function () {
              infoWindow.close();
            })
          );
          bounds.extend(marker.position);
          setMarkers((prevState) => [...prevState, marker]);
        }

        return <></>;
      });

      if (!bounds.isEmpty()) {
        map.setCenter(bounds.getCenter());
        map.fitBounds(bounds);
        mapListenersRef.current.push(
          window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
            if (map.getZoom() > 16) map.setZoom(16);
          })
        );
      }

      setMapMarkerSetting((prevState) => ({ ...prevState, ...systemMarkerSettings }));
    } else {
      clearMarkers();
    }
  };

  useEffect(() => {
    clearMarkers();
    clearListeners();
    mapMarker();
    return clearListeners;
    // eslint-disable-next-line
  }, [finalObj, map, mapMarkerUserSetting]);

  useEffect(() => {
    markers.forEach((marker) => {
      marker.setIcon({
        url: marker.getIcon().url,
        scaledSize: new window.google.maps.Size(mapMarkerSize, mapMarkerSize)
      });
    });
    // eslint-disable-next-line
  }, [mapMarkerSize]);

  useEffect(() => {
    if (!map) return;

    const executivePinInfoWindow = new window.google.maps.InfoWindow({
      disableAutoPan: true
    });

    const clearOverlay = () => {
      zoneOverlaysRef.current.forEach(polygon => polygon.setMap(null));
      zoneOverlaysRef.current = [];
    };

    const clearExecutiveOverlays = () => {
      executivePinInfoWindow.close();
      execPolygonsRef.current.forEach(p => p.setMap(null));
      execPolygonsRef.current = [];
      execPinsRef.current.forEach(m => {
        window.google.maps.event.clearInstanceListeners?.(m);
        m.setMap(null);
      });
      execPinsRef.current = [];
    };

    const emitExecutiveEntitySelected = (entity) => {
      if (!entity?.id) return;
      executivePinInfoWindow.close();
      window.dispatchEvent(new CustomEvent(
        MAP_EVENTS.EXECUTIVE_ENTITY_SELECTED,
        { detail: entity }
      ));
    };

    const addExecutivePinListeners = (marker, entity) => {
      const entityName = entity?.name || 'Strategic entity';

      marker.addListener('mouseover', () => {
        const label = document.createElement('div');
        label.textContent = entityName;
        label.style.fontWeight = '600';
        label.style.padding = '2px 4px';
        label.style.whiteSpace = 'nowrap';
        executivePinInfoWindow.setContent(label);
        executivePinInfoWindow.open(map, marker);
      });
      marker.addListener('mouseout', () => executivePinInfoWindow.close());

      if (entity?.id) {
        marker.addListener('click', () => emitExecutiveEntitySelected(entity));
      }
    };

    const saveLastVisitFromMap = () => {
      const c = map.getCenter();
      const payload = { lat: c.lat(), lng: c.lng(), zoom: map.getZoom() };
      window.localStorage.setItem('lastVisit', JSON.stringify(payload));
      setLat(payload.lat);
      setLng(payload.lng);
    };

    const onZone = (e) => {
      const payload = e.detail;
      clearOverlay();
      clearExecutiveOverlays();
      if (!payload) return;

      const zones = Array.isArray(payload)
        ? payload
        : Array.isArray(payload.zones)
          ? payload.zones
          : [payload];
      const allBounds = new window.google.maps.LatLngBounds();

      zones.forEach(zone => {
        if (Array.isArray(zone.bbox) && zone.bbox.length === 4) {
          const bounds = new window.google.maps.LatLngBounds(
            { lat: Number(zone.bbox[1]), lng: Number(zone.bbox[0]) },
            { lat: Number(zone.bbox[3]), lng: Number(zone.bbox[2]) }
          );
          allBounds.union(bounds);
        } else if (zone.centroid &&
                   !Number.isNaN(Number(zone.centroid.lat)) &&
                   !Number.isNaN(Number(zone.centroid.long))) {
          allBounds.extend(new window.google.maps.LatLng(
            Number(zone.centroid.lat),
            Number(zone.centroid.long)
          ));
        }

        const paths = parseWktPolygonToPaths(zone.wkt);
        if (paths.length) {
          paths[0].forEach(point => {
            allBounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
          });
          const polygon = new window.google.maps.Polygon({
            paths,
            clickable: false,
            strokeOpacity: 1,
            strokeWeight: 2,
            fillOpacity: 0.08
          });
          polygon.setMap(map);
          zoneOverlaysRef.current.push(polygon);
        }
      });

      if (!allBounds.isEmpty()) {
        map.fitBounds(allBounds);
        window.google.maps.event.addListenerOnce(map, 'idle', saveLastVisitFromMap);
      }
    };

    const onExecutive = (e) => {
      const payload = e.detail;
      clearOverlay();
      clearExecutiveOverlays();
      if (!payload) return;

      const zones = payload?.zones;
      const pinsFC = payload?.pins;
      const entitiesArr = payload?.entities;
      const executiveSettings = JSON.parse(window.localStorage.getItem('iconMapping') || '{}').executiveEntityMarkers;
      const executiveIcon = (entityId) => {
        const url = getExecutiveMarkerUrl(entityId, executiveSettings);
        return url ? { url, scaledSize: new window.google.maps.Size(mapMarkerSizeRef.current, mapMarkerSizeRef.current) } : undefined;
      };

      const allBounds = new window.google.maps.LatLngBounds();
      let touched = false;

      if (zones?.type === 'FeatureCollection' && Array.isArray(zones.features)) {
        zones.features.forEach((f) => {
          const g = f?.geometry;
          if (!g) return;
          let paths = [];
          if (g.type === 'Polygon') paths = geojsonPolygonToPaths(g.coordinates);
          else if (g.type === 'MultiPolygon') paths = geojsonMultiPolygonToPaths(g.coordinates);

          if (paths.length) {
            const poly = new window.google.maps.Polygon({
              paths,
              clickable: false,
              strokeOpacity: 1,
              strokeWeight: 2,
              fillOpacity: 0.08
            });
            poly.setMap(map);
            execPolygonsRef.current.push(poly);
            paths[0].forEach(pt => allBounds.extend(pt));
            touched = true;
          }
        });
      }

      if (Array.isArray(zones)) {
        zones.forEach((z) => {
          if (Array.isArray(z.bbox) && z.bbox.length === 4) {
            const bb = new window.google.maps.LatLngBounds(
              { lat: Number(z.bbox[1]), lng: Number(z.bbox[0]) },
              { lat: Number(z.bbox[3]), lng: Number(z.bbox[2]) }
            );
            allBounds.union(bb);
            touched = true;
          } else if (z.centroid && !Number.isNaN(z.centroid.lat) && !Number.isNaN(z.centroid.long)) {
            allBounds.extend(new window.google.maps.LatLng(Number(z.centroid.lat), Number(z.centroid.long)));
            touched = true;
          }

          if (z.wkt && typeof z.wkt === 'string') {
            const polys = parseWktToPolygons(z.wkt);
            polys.forEach((rings) => {
              const [outer, ...holes] = rings;
              if (!outer || !outer.length) return;
              outer.forEach(p => allBounds.extend(new window.google.maps.LatLng(p.lat, p.lng)));
              const poly = new window.google.maps.Polygon({
                paths: holes.length ? [outer, ...holes] : outer,
                clickable: false,
                strokeOpacity: 1,
                strokeWeight: 2,
                fillOpacity: 0.08
              });
              poly.setMap(map);
              execPolygonsRef.current.push(poly);
              touched = true;
            });
          }
        });
      }

      if (pinsFC?.type === 'FeatureCollection' && Array.isArray(pinsFC.features)) {
        pinsFC.features.forEach((f) => {
          const g = f?.geometry;
          if (g?.type === 'Point' && Array.isArray(g.coordinates)) {
            const [lng, lat] = g.coordinates;
            if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
              const m = new window.google.maps.Marker({
                position: { lat: Number(lat), lng: Number(lng) },
                map,
                icon: executiveIcon(f?.properties?.id),
                title: f?.properties?.name || 'Strategic entity'
              });
              addExecutivePinListeners(m, f?.properties);
              execPinsRef.current.push(m);
              allBounds.extend(m.getPosition());
              touched = true;
            }
          }
        });
      }

      if (Array.isArray(entitiesArr)) {
        entitiesArr.forEach((p) => {
          const lat = Number(p?.lat ?? p?.location_latitude);
          const lng = Number(p?.long ?? p?.location_longitude);
          if (Number.isNaN(lat) || Number.isNaN(lng)) return;
          const m = new window.google.maps.Marker({
            position: { lat, lng },
            map,
            icon: executiveIcon(p?.id),
            title: p?.name || 'Strategic entity'
          });
          addExecutivePinListeners(m, p);
          execPinsRef.current.push(m);
          allBounds.extend(m.getPosition());
          touched = true;
        });
      }

      if (touched && !allBounds.isEmpty()) {
        map.fitBounds(allBounds);
        window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
          if (map.getZoom() > 12) map.setZoom(12);
        });
        window.google.maps.event.addListenerOnce(map, 'idle', saveLastVisitFromMap);
      }
    };

    window.addEventListener(MAP_EVENTS.ZONE, onZone);
    window.addEventListener(MAP_EVENTS.EXECUTIVE, onExecutive);
    return () => {
      window.removeEventListener(MAP_EVENTS.ZONE, onZone);
      window.removeEventListener(MAP_EVENTS.EXECUTIVE, onExecutive);
      executivePinInfoWindow.close();
      clearOverlay();
      clearExecutiveOverlays();
    };
  }, [map, setLat, setLng]);

  const clearMarkers = () => {
    for (let i = 0; i < markers.length; i++) {
      markers[i].setMap(null);
    }
    setMarkers([]);
  };

  const clearListeners = () => {
    mapListenersRef.current.forEach((listener) => {
      window.google.maps.event.removeListener(listener);
    });
    mapListenersRef.current = [];
  };

  return (
    <div
      ref={ref}
      style={{ width: '100%', height: '101%' }}
      onClick={() => { if (collapseIsOpen) setCollapseIsOpen(false); }}
    />
  );
};

export default React.memo(GMap);
